import axios from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { omit } from 'lodash'

import { OpenPayCard } from '@/interfaces/openPayCard'

interface Params {
	userId: string | number
	cardId?: string | number
}

interface CreateCardParams {
	userId: string | number
	[key: string]: any
}

interface Props {
	immediate?: boolean
	params: Params
}

const getUserGuaranteeCards = async (
	params: Params
): Promise<OpenPayCard[]> => {
	const { data } = await axios.get<OpenPayCard[]>(
		`/api/users/${params.userId}/guarantee-cards`
	)

	return data
}

const postUserGuaranteeCard = async (
	cardData: CreateCardParams
): Promise<OpenPayCard> => {
	const { data } = await axios.post<OpenPayCard>(
		`/api/users/${cardData.userId}/guarantee-cards`,
		omit(cardData, ['userId'])
	)

	return data
}

const postUserGuaranteeCardSetDefault = async (
	params: Params
): Promise<OpenPayCard> => {
	const { data } = await axios.post<OpenPayCard>(
		`/api/users/${params.userId}/guarantee-cards/${params.cardId}/default`
	)

	return data
}

const deleteUserGuaranteeCard = async (params: Params) => {
	const { data } = await axios.delete(
		`/api/users/${params.userId}/guarantee-cards/${params.cardId}`
	)

	return data
}

const useUserGuaranteeCards = (props: Props) => {
	const { params, immediate = true } = props || {}
	const queryClient = useQueryClient()

	const { data, isLoading, isFetching, refetch } = useQuery(
		['userGuaranteeCards', params.userId],
		() => getUserGuaranteeCards({ userId: params.userId }),
		{
			enabled: immediate,
			retry: false,
		}
	)

	const createCardMutation = useMutation({
		mutationFn: postUserGuaranteeCard,
	})

	const setDefaultCardMutation = useMutation({
		mutationFn: postUserGuaranteeCardSetDefault,
		onSuccess: () => {
			queryClient.invalidateQueries(['userGuaranteeCards', params.userId])
		},
	})

	const deleteCardMutation = useMutation({
		mutationFn: deleteUserGuaranteeCard,
		onSuccess: () => {
			queryClient.invalidateQueries(['userGuaranteeCards', params.userId])
		},
	})

	return {
		userGuaranteeCards: data,
		isLoading: isLoading || isFetching,
		refetch,
		createCard: createCardMutation.mutateAsync,
		isCreatingCard: createCardMutation.isLoading,
		setDefaultCard: setDefaultCardMutation.mutateAsync,
		isSettingDefaultCard: setDefaultCardMutation.isLoading,
		deleteCard: deleteCardMutation.mutateAsync,
		isDeletingCard: deleteCardMutation.isLoading,
	}
}

export default useUserGuaranteeCards
